
import { Article, Collection, ArticleDetails } from '../types.ts';

const parseComposition = (composition: string) => {
  const coMatch = composition.match(/(\d+)% CO/);
  const elMatch = composition.match(/(\d+)% EL/);
  return {
    cottonPercentage: coMatch ? parseInt(coMatch[1], 10) : 100,
    elastanePercentage: elMatch ? parseInt(elMatch[1], 10) : 0,
  };
};

const parseArticleCode = (code: string): Omit<ArticleDetails, 'cottonPercentage' | 'elastanePercentage'> => {
  const parts = code.split('|');
  const elasticityMap: { [key: string]: string } = {
    'RG': 'Rigido',
    'EL': 'Elastico',
    'EB': 'Elastico Basso',
    '': 'Rigido'
  };
  const elasticity = elasticityMap[parts[1]] || 'Sconosciuto';
  const fabricCode = parts[2] || '';
  const drawing = fabricCode.substring(0, 2);
  const warp = fabricCode.substring(2, 4);
  const weft = fabricCode.substring(4, 6);
  const color = parts[3] || 'N/D';
  const finishing = parts[4] || 'N/D';

  return { elasticity, drawing, warp, weft, color, finishing };
};

type RawArticle = {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'obsolete';
  managed: boolean;
  composition: string;
  weightOz: number;
  warpWeightGpm: number;
  weftWeightGpm: number;
  finishingCostPerMeter: number;
};


// RAW DATA with new detailed fields
const RAW_ARTICLES: RawArticle[] = [
  { id: 'RR4567', name: 'Indigo Dream Stretch', code: 'PT|EL|RR4567|INDIGO|LW', status: 'active', managed: true, composition: '98% CO, 2% EL', weightOz: 12.5, warpWeightGpm: 250, weftWeightGpm: 170, finishingCostPerMeter: 0.8 },
  { id: 'KR8812', name: 'Vintage Rigid Blue', code: 'PT|RG|KR8812|VINTAGE|HW', status: 'active', managed: true, composition: '100% CO', weightOz: 14.0, warpWeightGpm: 290, weftWeightGpm: 180, finishingCostPerMeter: 0.65 },
  { id: 'LR8901', name: 'Black Coated Denim', code: 'PT|EL|LR8901|BLACK|MC', status: 'active', managed: true, composition: '99% CO, 1% EL', weightOz: 11.0, warpWeightGpm: 220, weftWeightGpm: 150, finishingCostPerMeter: 1.1 },
  { id: 'XF1234', name: 'Obsolete Red Weft', code: 'PT|RG|XF1234|RED|OW', status: 'obsolete', managed: true, composition: '100% CO', weightOz: 13.0, warpWeightGpm: 270, weftWeightGpm: 175, finishingCostPerMeter: 0.5 },
  { id: 'YM5566', name: 'Unmanaged Grey Twill', code: 'PT|RG|YM5566|GREY|TW', status: 'active', managed: false, composition: '80% CO, 20% PES', weightOz: 10.5, warpWeightGpm: 210, weftWeightGpm: 140, finishingCostPerMeter: 0.45 },
  { id: 'DF2024', name: 'Featherlight Chambray', code: 'PT|RG|DF2024|SKY|CH', status: 'active', managed: true, composition: '100% CO', weightOz: 6.0, warpWeightGpm: 100, weftWeightGpm: 80, finishingCostPerMeter: 0.9 },
  { id: 'SL9900', name: 'Selvedge Heavyweight', code: 'PT|RG|SL9900|RAW|SV', status: 'active', managed: true, composition: '100% CO', weightOz: 16.5, warpWeightGpm: 350, weftWeightGpm: 210, finishingCostPerMeter: 0.3 },
  { id: 'JG3344', name: 'Jungle Green Canvas', code: 'PT|RG|JG3344|JUNGLE|CV', status: 'active', managed: true, composition: '100% CO', weightOz: 13.0, warpWeightGpm: 280, weftWeightGpm: 180, finishingCostPerMeter: 0.55 },
];

const MOCK_COLLECTIONS: Collection[] = [
  { id: 'fw25', name: 'Fall/Winter 25', articles: ['RR4567', 'KR8812', 'SL9900'], isOfficial: true },
  { id: 'ss26', name: 'Spring/Summer 26', articles: ['DF2024', 'LR8901'], isOfficial: false },
];

const MOCK_ARTICLES: Article[] = RAW_ARTICLES.map(raw => {
  const { warpWeightGpm, weftWeightGpm, ...rest } = raw;
  const codeDetails = parseArticleCode(raw.code);
  const compositionDetails = parseComposition(raw.composition);
  return {
    ...rest,
    details: {
        ...codeDetails,
        ...compositionDetails,
        warpWeightGpm: warpWeightGpm,
        weftWeightGpm: weftWeightGpm,
    }
  };
});

const api = {
  fetchArticles: async (): Promise<Article[]> => {
    console.log("API: Fetching articles...");
    return new Promise(resolve => setTimeout(() => resolve(MOCK_ARTICLES), 500));
  },
  fetchCollections: async (): Promise<Collection[]> => {
    console.log("API: Fetching collections...");
    return new Promise(resolve => setTimeout(() => resolve(MOCK_COLLECTIONS), 500));
  },
  saveCollection: async (collection: Omit<Collection, 'id'> & { id?: string }): Promise<Collection> => {
    console.log("API: Saving collection...", collection);
    return new Promise(resolve => {
      setTimeout(() => {
        if (collection.id && collection.id !== 'new') {
          const index = MOCK_COLLECTIONS.findIndex(c => c.id === collection.id);
          if (index !== -1) {
            MOCK_COLLECTIONS[index] = { ...MOCK_COLLECTIONS[index], ...collection, id: collection.id };
            resolve(MOCK_COLLECTIONS[index]);
          }
        }
        const newId = `coll_${Date.now()}`;
        const newCollection: Collection = { ...collection, id: newId };
        MOCK_COLLECTIONS.push(newCollection);
        resolve(newCollection);
      }, 800)
    });
  }
};

export default api;
